import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { getJson } from '@/lib/http';
import { useAuth } from '@/lib/auth-store';
import { canMakePhotoChecksLive, type PhotoChecksMeta } from '@/lib/photoChecksAccess';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

type SubmissionType = 'POSTING' | 'REMOVAL' | 'REPOST';

type PromptConfig = {
  id: number;
  submissionType: SubmissionType;
  name: string;
  version: number;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED' | string;
  isActive: boolean;
  model: string;
  temperature: number;
  systemPrompt: string;
  userPromptTemplate: string;
  notes: string | null;
  publishedAt?: string | null;
  createdAt?: string | null;
};

type PromptListResponse = { ok: true; prompts: PromptConfig[] };
type ModelsResponse = {
  ok: true;
  models: string[];
  catalog?: Array<{
    id: string;
    provider: 'openai' | 'gemini';
    label: string;
    notes: string;
  }>;
};
type PromptResponse = { ok: true; prompt: PromptConfig };
type PromptDefaultsResponse = {
  ok: true;
  defaults: {
    submissionType: SubmissionType;
    name: string;
    model: string;
    temperature: number;
    systemPrompt: string;
    userPromptTemplate: string;
    notes: string | null;
  };
};

type PromptTestResponse = {
  ok: true;
  testRun: {
    submissionId: number;
    promptConfigId: number;
    promptVersion: number;
    promptStatus: string;
    model: string;
    modelVersion: string;
    latencyMs: number;
    result: {
      flagged: boolean;
      reasons: string[];
      confidence: number;
      observations: Record<string, unknown>;
      computedVerdict?: {
        verdict: 'PASS' | 'PARTIAL_FAIL' | 'FAIL';
        reasons: string[];
        reasonCodes: string[];
      } | null;
    };
  };
};

type EditorState = {
  id: number | null;
  submissionType: SubmissionType;
  name: string;
  model: string;
  temperature: string;
  systemPrompt: string;
  userPromptTemplate: string;
  notes: string;
  status: string | null;
};

const DEFAULT_EDITOR: EditorState = {
  id: null,
  submissionType: 'POSTING',
  name: '',
  model: '',
  temperature: '0',
  systemPrompt: '',
  userPromptTemplate: '',
  notes: '',
  status: null,
};
const POSTING_INSTRUCTIONS_PLACEHOLDER = '{{postingInstructionsText}}';

function hasRole(user: any, role: string): boolean {
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const legacyRole = typeof user?.role === 'string' ? user.role : '';
  const isSuperAdmin = roles.includes('superadmin')
    || roles.includes('super_admin')
    || legacyRole === 'superadmin'
    || legacyRole === 'super_admin';

  if (role.startsWith('AI_TRIAGE_') && isSuperAdmin) {
    return true;
  }
  return roles.includes(role);
}

export default function AITriagePromptStudioPage() {
  const { state } = useAuth();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [submissionTypeFilter, setSubmissionTypeFilter] = useState<SubmissionType>('POSTING');
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
  const [editor, setEditor] = useState<EditorState>(DEFAULT_EDITOR);
  const [testSubmissionId, setTestSubmissionId] = useState('');
  const [testResult, setTestResult] = useState<PromptTestResponse['testRun'] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const user = state.user;
  const canView = hasRole(user, 'AI_TRIAGE_VIEW');
  const canEdit = hasRole(user, 'AI_TRIAGE_PROMPT_EDIT');
  const canPublish = hasRole(user, 'AI_TRIAGE_PROMPT_PUBLISH');

  const metaQuery = useQuery({
    queryKey: ['/api/admin/ai-eval/meta'],
    queryFn: () => getJson<PhotoChecksMeta>('/api/admin/ai-eval/meta'),
    enabled: canView,
  });
  const canMakeLive = canMakePhotoChecksLive(user, metaQuery.data?.livePublishEnabled);

  const modelsQuery = useQuery<ModelsResponse>({
    queryKey: ['/api/admin/ai-triage/models'],
    queryFn: () => getJson('/api/admin/ai-triage/models'),
    enabled: canView,
  });

  const promptsQuery = useQuery<PromptListResponse>({
    queryKey: ['/api/admin/ai-triage/prompts', submissionTypeFilter],
    queryFn: () => getJson(`/api/admin/ai-triage/prompts?submissionType=${submissionTypeFilter}`),
    enabled: canView,
  });

  const defaultsQuery = useQuery<PromptDefaultsResponse>({
    queryKey: ['/api/admin/ai-triage/prompts/defaults', submissionTypeFilter],
    queryFn: () => getJson(`/api/admin/ai-triage/prompts/defaults?submissionType=${submissionTypeFilter}`),
    enabled: canView && (submissionTypeFilter === 'POSTING' || submissionTypeFilter === 'REMOVAL'),
    retry: false,
  });

  const prompts = promptsQuery.data?.prompts ?? [];
  const activePrompt = useMemo(
    () => prompts.find((p) => p.isActive && p.status === 'ACTIVE') ?? null,
    [prompts],
  );
  const selectedPrompt = useMemo(
    () => prompts.find((p) => p.id === selectedPromptId) ?? null,
    [prompts, selectedPromptId],
  );
  const promptTemplateValidationError = useMemo(() => {
    if (editor.submissionType !== 'POSTING') {
      return null;
    }
    if (!editor.userPromptTemplate.includes(POSTING_INSTRUCTIONS_PLACEHOLDER)) {
      return `POSTING prompts must include ${POSTING_INSTRUCTIONS_PLACEHOLDER} in the User Prompt Template.`;
    }
    return null;
  }, [editor.submissionType, editor.userPromptTemplate]);

  useEffect(() => {
    if (!selectedPrompt && prompts.length > 0) {
      setSelectedPromptId(prompts[0].id);
      return;
    }
    if (selectedPrompt) {
      setEditor({
        id: selectedPrompt.id,
        submissionType: selectedPrompt.submissionType,
        name: selectedPrompt.name,
        model: selectedPrompt.model,
        temperature: String(selectedPrompt.temperature ?? 0),
        systemPrompt: selectedPrompt.systemPrompt,
        userPromptTemplate: selectedPrompt.userPromptTemplate,
        notes: selectedPrompt.notes ?? '',
        status: selectedPrompt.status,
      });
    }
  }, [selectedPrompt, prompts]);

  useEffect(() => {
    if (!editor.model && (modelsQuery.data?.models?.length ?? 0) > 0) {
      setEditor((prev) => ({ ...prev, model: modelsQuery.data!.models[0] }));
    }
  }, [editor.model, modelsQuery.data]);

  useEffect(() => {
    if (prompts.length > 0 || selectedPromptId || editor.id) {
      return;
    }
    const defaults = defaultsQuery.data?.defaults;
    if (!defaults) {
      return;
    }

    setEditor({
      id: null,
      submissionType: defaults.submissionType,
      name: defaults.name,
      model: defaults.model || (modelsQuery.data?.models?.[0] ?? ''),
      temperature: String(defaults.temperature ?? 0),
      systemPrompt: defaults.systemPrompt,
      userPromptTemplate: defaults.userPromptTemplate,
      notes: defaults.notes ?? '',
      status: null,
    });
  }, [defaultsQuery.data, prompts.length, selectedPromptId, editor.id, modelsQuery.data]);

  const refreshPrompts = async () => {
    await queryClient.invalidateQueries({ queryKey: ['/api/admin/ai-triage/prompts', submissionTypeFilter] });
  };

  const seedDefaultMutation = useMutation({
    mutationFn: async (allowIfExists: boolean) => {
      const res = await apiRequest('/api/admin/ai-triage/prompts/seed-default-posting', 'POST', { allowIfExists });
      return res.json() as Promise<PromptResponse>;
    },
    onSuccess: async (payload) => {
      setMessage(`Seeded prompt v${payload.prompt.version}`);
      setErrorMessage(null);
      await refreshPrompts();
      setSelectedPromptId(payload.prompt.id);
    },
    onError: (error: any) => {
      setErrorMessage(error?.message ?? 'Failed to seed prompt');
      setMessage(null);
    },
  });

  const createPromptMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('/api/admin/ai-triage/prompts', 'POST', {
        submissionType: editor.submissionType,
        name: editor.name,
        model: editor.model,
        temperature: Number(editor.temperature || 0),
        systemPrompt: editor.systemPrompt,
        userPromptTemplate: editor.userPromptTemplate,
        notes: editor.notes || null,
      });
      return res.json() as Promise<PromptResponse>;
    },
    onSuccess: async (payload) => {
      setMessage(`Created draft v${payload.prompt.version}`);
      setErrorMessage(null);
      await refreshPrompts();
      setSelectedPromptId(payload.prompt.id);
    },
    onError: (error: any) => {
      setErrorMessage(error?.message ?? 'Failed to create prompt');
      setMessage(null);
    },
  });

  const updatePromptMutation = useMutation({
    mutationFn: async () => {
      if (!editor.id) throw new Error('No prompt selected');
      const res = await apiRequest(`/api/admin/ai-triage/prompts/${editor.id}`, 'PATCH', {
        name: editor.name,
        model: editor.model,
        temperature: Number(editor.temperature || 0),
        systemPrompt: editor.systemPrompt,
        userPromptTemplate: editor.userPromptTemplate,
        notes: editor.notes || null,
      });
      return res.json() as Promise<PromptResponse>;
    },
    onSuccess: async (payload) => {
      setMessage(`Saved draft v${payload.prompt.version}`);
      setErrorMessage(null);
      await refreshPrompts();
      setSelectedPromptId(payload.prompt.id);
    },
    onError: (error: any) => {
      setErrorMessage(error?.message ?? 'Failed to update prompt');
      setMessage(null);
    },
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      if (!editor.id) throw new Error('No prompt selected');
      const res = await apiRequest(`/api/admin/ai-triage/prompts/${editor.id}/publish`, 'POST');
      return res.json() as Promise<PromptResponse>;
    },
    onSuccess: async (payload) => {
      setMessage(`Published ${payload.prompt.submissionType} prompt v${payload.prompt.version}`);
      setErrorMessage(null);
      await refreshPrompts();
      setSelectedPromptId(payload.prompt.id);
    },
    onError: (error: any) => {
      setErrorMessage(error?.message ?? 'Failed to publish prompt');
      setMessage(null);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPromptId) throw new Error('No prompt selected');
      const res = await apiRequest(`/api/admin/ai-triage/prompts/${selectedPromptId}/archive`, 'POST');
      return res.json() as Promise<PromptResponse>;
    },
    onSuccess: async (payload) => {
      setMessage(`Archived prompt v${payload.prompt.version}`);
      setErrorMessage(null);
      await refreshPrompts();
      setSelectedPromptId(payload.prompt.id);
    },
    onError: (error: any) => {
      setErrorMessage(error?.message ?? 'Failed to archive prompt');
      setMessage(null);
    },
  });

  const cloneMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPromptId) throw new Error('No prompt selected');
      const res = await apiRequest(`/api/admin/ai-triage/prompts/${selectedPromptId}/clone`, 'POST');
      return res.json() as Promise<PromptResponse>;
    },
    onSuccess: async (payload) => {
      setMessage(`Cloned prompt to draft v${payload.prompt.version}`);
      setErrorMessage(null);
      await refreshPrompts();
      setSelectedPromptId(payload.prompt.id);
    },
    onError: (error: any) => {
      setErrorMessage(error?.message ?? 'Failed to clone prompt');
      setMessage(null);
    },
  });

  const testMutation = useMutation({
    mutationFn: async () => {
      if (!editor.id) throw new Error('Select a saved prompt before testing');
      const submissionId = Number(testSubmissionId);
      if (!Number.isInteger(submissionId) || submissionId <= 0) {
        throw new Error('Enter a valid submission ID');
      }
      const res = await apiRequest(`/api/admin/ai-triage/prompts/${editor.id}/test`, 'POST', { submissionId });
      return res.json() as Promise<PromptTestResponse>;
    },
    onSuccess: (payload) => {
      setTestResult(payload.testRun);
      setMessage(`Prompt test completed in ${payload.testRun.latencyMs}ms`);
      setErrorMessage(null);
    },
    onError: (error: any) => {
      setErrorMessage(error?.message ?? 'Failed to test prompt');
      setMessage(null);
    },
  });

  const isBusy = seedDefaultMutation.isPending
    || createPromptMutation.isPending
    || updatePromptMutation.isPending
    || publishMutation.isPending
    || archiveMutation.isPending
    || cloneMutation.isPending
    || testMutation.isPending;
  const saveDisabled = isBusy || !canEdit || Boolean(promptTemplateValidationError);
  const publishDisabled = isBusy || !canMakeLive || !editor.id || editor.status === 'ACTIVE' || Boolean(promptTemplateValidationError);
  const testDisabledReason = !editor.id
    ? 'Save or create a draft prompt first to enable testing.'
    : null;
  const isEditingExistingPrompt = Boolean(editor.id);
  const isSelectedDraft = editor.status === 'DRAFT' || !editor.id;
  const canEditFields = canEdit && !isBusy && isSelectedDraft;
  const canSaveCurrent = !saveDisabled && isSelectedDraft;
  const selectedPromptIsActive = Boolean(selectedPrompt?.isActive && selectedPrompt?.status === 'ACTIVE');
  const selectedPromptSummary = !editor.id
    ? 'Unsaved working copy'
    : `v${selectedPrompt?.version ?? '?'} • ${editor.status ?? 'UNKNOWN'}${selectedPromptIsActive ? ' • LIVE' : ''}`;

  if (!canView) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold">Edit photo-rule wording</h1>
        <p className="mt-2 text-sm text-muted-foreground">Permission required: AI_TRIAGE_VIEW</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="rounded-md border bg-muted/30 px-4 py-3 text-sm">
        <strong>Where you are:</strong> Photo Checks → edit what to look for.
        Use <strong>Make live</strong> only when you intend to replace the current live standard.
        To trial safely first: propose a change, then go to{' '}
        <button type="button" className="underline font-medium" onClick={() => setLocation('/admin/ai/results')}>
          Try a change
        </button>
        {' · '}
        <button type="button" className="underline font-medium" onClick={() => setLocation('/admin/ai')}>
          Home
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Edit what to look for</h1>
          <p className="text-sm text-muted-foreground">
            Plain-language standards Photo Checks follows when reviewing posting photos. Drafts stay off live until you Make live.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="h-10 rounded-md border bg-background px-3 text-sm"
            value={submissionTypeFilter}
            onChange={(e) => {
              setSubmissionTypeFilter(e.target.value as SubmissionType);
              setSelectedPromptId(null);
              setTestResult(null);
            }}
            disabled={isBusy}
          >
            <option value="POSTING">POSTING</option>
            <option value="REMOVAL">REMOVAL</option>
            <option value="REPOST">REPOST</option>
          </select>
          <Button variant="outline" onClick={() => void refreshPrompts()} disabled={isBusy}>
            Refresh
          </Button>
          {canEdit && submissionTypeFilter === 'POSTING' && (
            <Button
              variant="outline"
              onClick={() => seedDefaultMutation.mutate(false)}
              disabled={isBusy}
            >
              Seed Default POSTING
            </Button>
          )}
        </div>
      </div>

      {message && <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">{message}</div>}
      {errorMessage && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{errorMessage}</div>}

      <div className="rounded-md border bg-muted/20 px-4 py-3 text-sm">
        {activePrompt ? (
          <div>
            <strong>Live photo rules:</strong> {activePrompt.name} (v{activePrompt.version}) using <code>{activePrompt.model}</code>.
          </div>
        ) : (
          <div>
            <strong>Live photo rules:</strong> No live version for {submissionTypeFilter}. Photo Checks will use the server fallback.
          </div>
        )}
      </div>

      <div className="rounded-md border bg-blue-50 px-4 py-3 text-sm text-blue-900">
        <strong>How versioning works:</strong> <strong>Save Draft</strong> only updates the selected draft. <strong>Make live</strong> is the step that changes live Photo Checks. To propose another change, use <strong>Propose change (clone)</strong> or <strong>New blank draft</strong> + <strong>Create Draft</strong>.
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[360px_1fr]">
        <div className="rounded-lg border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-medium">Prompt Library</h2>
            <span className="text-xs text-muted-foreground">{prompts.length} items</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Each saved draft or published prompt is a library item. Use <strong>Propose change (clone)</strong> to create a new version.
          </p>

          {promptsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading prompts…</p>
          ) : prompts.length === 0 ? (
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">No prompt library items yet for {submissionTypeFilter}.</p>
              {(submissionTypeFilter === 'POSTING' || submissionTypeFilter === 'REMOVAL') && defaultsQuery.data?.defaults && (
                <p className="text-xs text-muted-foreground">
                  The editor is preloaded with the current server fallback prompt for {submissionTypeFilter}. {submissionTypeFilter === 'POSTING' ? 'Save or seed' : 'Save'} to add the first library item.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2 max-h-[70vh] overflow-auto pr-1">
              {prompts.map((prompt) => (
                <button
                  key={prompt.id}
                  type="button"
                  onClick={() => {
                    setSelectedPromptId(prompt.id);
                    setTestResult(null);
                  }}
                  className={`w-full rounded border p-3 text-left text-sm ${
                    selectedPromptId === prompt.id ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{prompt.name}</span>
                    <div className="flex items-center gap-1">
                      {prompt.isActive && (
                        <span className="rounded border border-green-200 bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700">
                          ACTIVE
                        </span>
                      )}
                      <span className="text-xs">{prompt.status}</span>
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    v{prompt.version} • {prompt.model} • #{prompt.id}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border bg-card p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-medium">{editor.id ? `Library Item #${editor.id}` : 'New Draft Prompt'}</h2>
            <div className="flex flex-wrap gap-2">
              {canEdit && selectedPromptId && (
                <Button variant="outline" onClick={() => cloneMutation.mutate()} disabled={isBusy}>
                  Propose change (clone)
                </Button>
              )}
              {canEdit && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditor({
                        ...DEFAULT_EDITOR,
                        submissionType: submissionTypeFilter,
                        model: modelsQuery.data?.models?.[0] ?? '',
                      });
                      setSelectedPromptId(null);
                      setTestResult(null);
                    }}
                    disabled={isBusy}
                  >
                    New blank draft
                  </Button>
                  <Button
                    onClick={() => (editor.id ? updatePromptMutation.mutate() : createPromptMutation.mutate())}
                    disabled={!canSaveCurrent}
                  >
                    {editor.id ? 'Save Draft Changes' : 'Create Draft'}
                  </Button>
                </>
              )}
              {canPublish && editor.id && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => publishMutation.mutate()}
                    disabled={publishDisabled}
                    title={!canMakeLive ? 'Live publish is off (PHOTO_CHECKS_LIVE_PUBLISH_ENABLED)' : undefined}
                  >
                    Make live
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => archiveMutation.mutate()}
                    disabled={isBusy || !editor.id || editor.status === 'ARCHIVED'}
                  >
                    Archive
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="rounded-md border px-3 py-2 text-xs">
            <div className="font-medium">Current editor state</div>
            <div className="mt-1 text-muted-foreground">
              {selectedPromptSummary}
              {editor.id && editor.status !== 'DRAFT' ? ' (read-only until you clone to a draft)' : ''}
            </div>
          </div>

          <div className="rounded-md border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
            {isEditingExistingPrompt ? (
              <span>
                {editor.status === 'DRAFT'
                  ? 'You are editing a draft. Save Draft Changes updates this draft only. It does not change live Photo Checks until you click Make live.'
                  : `You are viewing a ${String(editor.status || 'saved').toLowerCase()} version. It is read-only. Use Propose change (clone) to make an editable draft, then Make live to switch live Photo Checks.`}
              </span>
            ) : (
              <span>
                This editor is not saved yet. Click <strong>Create Draft</strong> to add a new library item to the list.
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-sm font-medium">Name</label>
              <Input
                value={editor.name}
                onChange={(e) => setEditor((prev) => ({ ...prev, name: e.target.value }))}
                disabled={!canEditFields}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Submission Type</label>
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={editor.submissionType}
                onChange={(e) => setEditor((prev) => ({ ...prev, submissionType: e.target.value as SubmissionType }))}
                disabled={!canEditFields || Boolean(editor.id)}
              >
                <option value="POSTING">POSTING</option>
                <option value="REMOVAL">REMOVAL</option>
                <option value="REPOST">REPOST</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Model (judgment engine)</label>
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={editor.model}
                onChange={(e) => setEditor((prev) => ({ ...prev, model: e.target.value }))}
                disabled={!canEditFields}
              >
                <option value="">Select model</option>
                {(modelsQuery.data?.catalog?.length
                  ? modelsQuery.data.catalog
                  : (modelsQuery.data?.models ?? []).map((id) => ({
                    id,
                    provider: id.startsWith('gemini') ? 'gemini' as const : 'openai' as const,
                    label: id,
                    notes: '',
                  }))
                ).map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.label} · {entry.provider}{entry.notes ? ` — ${entry.notes}` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Choose OpenAI or Gemini on a draft, then trial it in Photo Checks. Live publish stays off until the env switch is set.
              </p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Temperature</label>
              <Input
                value={editor.temperature}
                onChange={(e) => setEditor((prev) => ({ ...prev, temperature: e.target.value }))}
                disabled={!canEditFields}
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Notes</label>
            <Input
              value={editor.notes}
              onChange={(e) => setEditor((prev) => ({ ...prev, notes: e.target.value }))}
              disabled={!canEditFields}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">System Prompt</label>
            <Textarea
              value={editor.systemPrompt}
              onChange={(e) => setEditor((prev) => ({ ...prev, systemPrompt: e.target.value }))}
              rows={12}
              disabled={!canEditFields}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">User Prompt Template</label>
            <Textarea
              value={editor.userPromptTemplate}
              onChange={(e) => setEditor((prev) => ({ ...prev, userPromptTemplate: e.target.value }))}
              rows={10}
              disabled={!canEditFields}
            />
            <p className="text-xs text-muted-foreground">
              {editor.submissionType === 'POSTING'
                ? <>Use <code>{POSTING_INSTRUCTIONS_PLACEHOLDER}</code> placeholder for posting instructions context.</>
                : editor.submissionType === 'REMOVAL'
                  ? 'REMOVAL prompts should focus on complete removal, residue/fragments, and full target-area visibility.'
                  : 'Customize the user prompt template for the selected submission type.'}
            </p>
            {promptTemplateValidationError && (
              <p className="text-xs text-red-600">
                {promptTemplateValidationError}
              </p>
            )}
          </div>

          <div className="rounded-md border p-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-medium text-sm">Test Prompt (No Job Created)</h3>
              {testMutation.isPending && <span className="text-xs text-muted-foreground">Running test…</span>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                placeholder="Submission ID"
                value={testSubmissionId}
                onChange={(e) => setTestSubmissionId(e.target.value)}
                className="w-40"
                disabled={isBusy}
              />
              <Button
                variant="outline"
                onClick={() => testMutation.mutate()}
                disabled={isBusy || !editor.id}
              >
                Test Prompt
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Tests run the currently selected saved prompt version only. Testing does not publish or enqueue any jobs.
              {editor.submissionType === 'REMOVAL' ? ' REMOVAL tests use the submission’s removal-after evidence image.' : ''}
            </p>
            {testDisabledReason && (
              <p className="text-xs text-muted-foreground">
                {testDisabledReason}
              </p>
            )}

            {testResult && (
              <div className="rounded border bg-muted/20 p-3 text-sm space-y-2">
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  <span><strong>Submission:</strong> {testResult.submissionId}</span>
                  <span><strong>Prompt:</strong> v{testResult.promptVersion} ({testResult.promptStatus})</span>
                  <span><strong>Model:</strong> {testResult.modelVersion || testResult.model}</span>
                  <span><strong>Latency:</strong> {testResult.latencyMs}ms</span>
                </div>
                {testResult.result.computedVerdict && (
                  <div>
                    <div><strong>AI Verdict:</strong> {testResult.result.computedVerdict.verdict}</div>
                    {testResult.result.computedVerdict.reasons.length > 0 && (
                      <ul className="list-disc pl-5 mt-1">
                        {testResult.result.computedVerdict.reasons.slice(0, 6).map((r, idx) => (
                          <li key={`${idx}-${r}`}>{r}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                <details>
                  <summary className="cursor-pointer">Observations JSON</summary>
                  <pre className="mt-2 max-h-72 overflow-auto rounded bg-background p-2 text-xs">
                    {JSON.stringify(testResult.result.observations, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
